import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaUsers, 
  FaAward, 
  FaHandshake, 
  FaGlobe, 
  FaShieldAlt,
  FaRocket,
  FaHeart,
  FaChartLine,
  FaUserTie,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import TeamMember from '../components/TeamMember';

const AboutPage = () => {
  const values = [
    {
      icon: <FaShieldAlt className="text-3xl" />,
      title: 'Integrity',
      description: 'We maintain the highest ethical standards in all our dealings.'
    },
    {
      icon: <FaHandshake className="text-3xl" />,
      title: 'Trust',
      description: 'Building lasting relationships based on transparency and reliability.'
    },
    {
      icon: <FaRocket className="text-3xl" />,
      title: 'Excellence',
      description: 'Striving for perfection in every visa application we handle.'
    },
    {
      icon: <FaHeart className="text-3xl" />,
      title: 'Empathy',
      description: 'Understanding your unique situation and treating you like family.'
    }
  ];

  const milestones = [
    { year: '2010', title: 'Founded', description: 'Started with a small office in New York' },
    { year: '2012', title: '1000+ Visas', description: 'Processed over 1000 successful applications' },
    { year: '2015', title: 'Expansion', description: 'Opened offices in London and Dubai' },
    { year: '2018', title: 'Award', description: 'Best Visa Agency Award by Travel Magazine' },
    { year: '2020', title: 'Online Platform', description: 'Launched digital application system' },
    { year: '2023', title: '10,000+', description: 'Celebrated 10,000 successful visa applications' }
  ];

  const team = [
    {
      name: 'Sarah Johnson',
      role: 'Founder & CEO',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&h=400&q=80',
      experience: '15+ years in immigration law',
      description: 'Former immigration officer with deep knowledge of visa regulations'
    },
    {
      name: 'Michael Chen',
      role: 'Head of Operations',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&h=400&q=80',
      experience: '12+ years in visa processing',
      description: 'Specializes in complex visa cases and appeals'
    },
    {
      name: 'Elena Rodriguez',
      role: 'Schengen Visa Specialist',
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&h=400&q=80',
      experience: '10+ years European visas',
      description: 'Multilingual expert in European immigration'
    },
    {
      name: 'David Kim',
      role: 'Student Visa Director',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&h=400&q=80',
      experience: '8+ years education visas',
      description: 'Helped 500+ students achieve their study abroad dreams'
    }
  ];

  const offices = [
    {
      city: 'New York',
      address: '123 Visa Street, Suite 100, NY 10001',
      phone: '0313-5993810',
      email: 'ny@globalvisa.com',
      hours: 'Mon-Fri: 9AM-7PM, Sat: 10AM-4PM'
    },
    {
      city: 'London',
      address: '45 Immigration Road, London W1B 5JH',
      phone: '+44 20 7123 4567',
      email: 'london@globalvisa.com',
      hours: 'Mon-Fri: 9AM-6PM, Sat: 10AM-3PM'
    },
    {
      city: 'Dubai',
      address: 'Business Bay, Tower 2, Office 1204',
      phone: '+971 4 123 4567',
      email: 'dubai@globalvisa.com',
      hours: 'Sun-Thu: 9AM-6PM'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary-500 rounded-full blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-primary-400 rounded-full blur-3xl opacity-20"></div>
        </div>
        
        <div className="relative container-padding py-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-6xl font-bold mb-6"
            >
              Your Trusted Partner in 
              <span className="text-primary-200"> Visa Success</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-primary-100 mb-8"
            >
              For over a decade, we've been transforming visa dreams into reality with 
              expertise, integrity, and personalized service.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3"
            >
              <FaChartLine className="mr-3" />
              <span className="font-medium">95% Success Rate • 10,000+ Happy Clients</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="container-padding">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Founded in 2010 by Sarah Johnson, a former immigration officer, GlobalVisa 
                  started with a simple mission: to make visa applications accessible, 
                  transparent, and successful for everyone.
                </p>
                <p>
                  What began as a small consultation service has grown into a global 
                  visa agency with offices in three countries, serving clients from 
                  over 50 nations.
                </p>
                <p>
                  Our team combines decades of immigration experience with cutting-edge 
                  technology to provide efficient, reliable visa services. We understand 
                  that behind every application is a dream - whether it's studying abroad, 
                  reuniting with family, or exploring new horizons.
                </p>
              </div>
              
              <div className="mt-8 grid grid-cols-2 gap-6">
                <div className="bg-primary-50 rounded-xl p-6">
                  <div className="text-3xl font-bold text-primary-600 mb-2">10+</div>
                  <div className="text-gray-700">Years Experience</div>
                </div>
                <div className="bg-primary-50 rounded-xl p-6">
                  <div className="text-3xl font-bold text-primary-600 mb-2">50+</div>
                  <div className="text-gray-700">Countries Covered</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden mb-6">
                  <img 
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
                    alt="Our Team"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 mr-4">
                    <FaUserTie className="text-xl" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Sarah Johnson</h4>
                    <p className="text-gray-600">Founder & CEO</p>
                  </div>
                </div>
                <blockquote className="mt-6 text-gray-700 italic border-l-4 border-primary-500 pl-4">
                  "Every visa application is a personal journey. We're here to guide you 
                  through every step with care and expertise."
                </blockquote>
              </div>
              
              <div className="absolute -z-10 -top-6 -right-6 w-48 h-48 bg-primary-200 rounded-full blur-3xl opacity-30"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-gray-50 rounded-2xl p-8 text-center hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600">
              Milestones in our growth and success
            </p>
          </div>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary-200"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex flex-col lg:flex-row items-center ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Year circle */}
                  <div className="relative z-10 lg:w-1/2 flex justify-center lg:justify-end">
                    <div className={`w-24 h-24 rounded-full bg-primary-600 text-white flex flex-col items-center justify-center ${
                      index % 2 === 0 ? 'lg:mr-8' : 'lg:ml-8'
                    }`}>
                      <div className="text-2xl font-bold">{milestone.year}</div>
                      <div className="text-sm">Year</div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="lg:w-1/2 mt-6 lg:mt-0">
                    <div className={`bg-white rounded-2xl shadow-lg p-6 ${
                      index % 2 === 0 ? 'lg:ml-8' : 'lg:mr-8 lg:text-right'
                    }`}>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="py-20 bg-white">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dedicated experts with extensive experience in immigration law and visa processing
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <TeamMember key={member.name} member={member} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Our Offices */}
      <section className="py-20 bg-gray-50">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Global Offices
            </h2>
            <p className="text-xl text-gray-600">
              Serving clients across multiple time zones
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {offices.map((office, index) => (
              <motion.div
                key={office.city}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mr-4">
                      <FaMapMarkerAlt />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{office.city}</h3>
                      <p className="text-gray-600">Main Office</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <FaMapMarkerAlt className="text-gray-400 mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{office.address}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <FaPhone className="text-gray-400 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{office.phone}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <FaEnvelope className="text-gray-400 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{office.email}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <FaClock className="text-gray-400 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{office.hours}</span>
                    </div>
                  </div>
                  
                  <button className="w-full mt-8 btn-secondary">
                    Contact This Office
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-900 to-primary-700 text-white">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-primary-100">
              Join thousands of satisfied clients who have achieved their visa dreams 
              with our expert guidance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary bg-white text-primary-700 hover:bg-primary-100 px-8 py-4 text-lg"
              >
                Start Your Application
              </Link>
              <Link
                to="/contact"
                className="btn-secondary border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;